import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Empty, Space, Typography, message } from 'antd';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import './AIDatasetLessonPlayer.css';
import { TAIDatasetLessonConfig, TAIDatasetEntry } from '../../../models/courses/aiDataset';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';
import { useUserApi } from '../../../api/user/userApp';

type LanguageKey = 'fr' | 'en' | 'ar';

type AIDatasetDragItem = {
  id: string;
  label: string;
  expectedKey: string | null;
};

type AIDatasetTarget = {
  id: string;
  label: string;
};

type ListsState = {
  pool: AIDatasetDragItem[];
  lists: Record<string, AIDatasetDragItem[]>;
};

interface AIDatasetLessonPlayerProps {
  config: TAIDatasetLessonConfig;
  onComplete: () => void;
  statement?: string;
  lessonId?: number;
}

const shuffleArray = <T,>(arr: T[]): T[] =>
  arr
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);

const getLanguageKey = (language: string): LanguageKey => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('ar')) return 'ar';
  return 'en';
};

const getFeaturesByLanguage = (entry: TAIDatasetEntry, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return entry.features_fr ?? entry.features_en ?? entry.features_ar ?? [];
    case 'ar':
      return entry.features_ar ?? entry.features_en ?? entry.features_fr ?? [];
    default:
      return entry.features_en ?? entry.features_fr ?? entry.features_ar ?? [];
  }
};

const getNoiseByLanguage = (config: TAIDatasetLessonConfig, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return config.bruit_fr ?? config.bruit_en ?? config.bruit_ar ?? [];
    case 'ar':
      return config.bruit_ar ?? config.bruit_en ?? config.bruit_fr ?? [];
    default:
      return config.bruit_en ?? config.bruit_fr ?? config.bruit_ar ?? [];
  }
};

const AIDatasetLessonPlayer: React.FC<AIDatasetLessonPlayerProps> = ({ config, onComplete, statement, lessonId }) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageKey(i18n.language || 'en');

  // Hash de configuration pour invalider un ancien workspace si la config a changé
  const configHash = useMemo(() => {
    try {
      const base = JSON.stringify({
        dataset: config?.dataset || {},
        bruit_fr: config?.bruit_fr || [],
        bruit_en: config?.bruit_en || [],
        bruit_ar: config?.bruit_ar || [],
      });
      return base.length;
    } catch {
      return 0;
    }
  }, [config]);

  const { targets, initialItems } = useMemo(() => {
    const entries = Object.entries(config.dataset || {});
    const builtTargets: AIDatasetTarget[] = entries.map(([id, entry]) => ({
      id,
      label: entry.key,
    }));

    const expectedItems: AIDatasetDragItem[] = entries.flatMap(([id, entry]) =>
      getFeaturesByLanguage(entry, language).map((feature, index) => ({
        id: `${id}-feature-${index}`,
        label: feature,
        expectedKey: id,
      })),
    );

    const noiseItems: AIDatasetDragItem[] = getNoiseByLanguage(config, language).map((noise, index) => ({
      id: `noise-${index}`,
      label: noise,
      expectedKey: null,
    }));

    return {
      targets: builtTargets,
      initialItems: shuffleArray([...expectedItems, ...noiseItems]),
    };
  }, [config, language]);

  const [state, setState] = useState<ListsState>({
    pool: initialItems,
    lists: Object.fromEntries(targets.map((target) => [target.id, []])),
  });

  const { getUserAnswerById, saveUserAnswerById } = useUserApi();

  // Blocks the async workspace restore from replacing lists mid-drag
  const isDraggingRef = useRef(false);

  useEffect(() => {
    setState({
      pool: initialItems,
      lists: Object.fromEntries(targets.map((target) => [target.id, []])),
    });
  }, [initialItems, targets]);

  // Load saved workspace if available (restore assignments and pool)
  useEffect(() => {
    if (!lessonId) return;
    const byId = new Map<string, AIDatasetDragItem>();
    initialItems.forEach((it) => byId.set(it.id, it));
    getUserAnswerById(lessonId)
      .then((resp: any) => {
        if (isDraggingRef.current) return; // never replace lists under an active drag
        const workspace: string | undefined = resp?.data?.workspace;
        if (!workspace) return;
        try {
          const parsed = JSON.parse(workspace);
          if (!parsed || parsed.configHash !== configHash) {
            // Config a changé: ignorer l'ancien workspace
            return;
          }
          if (parsed && typeof parsed === 'object') {
            const restoredAssignments: Record<string, AIDatasetDragItem[]> = {};
            if (parsed.assignments && typeof parsed.assignments === 'object') {
              Object.keys(parsed.assignments).forEach((k) => {
                const arr: any[] = Array.isArray(parsed.assignments[k]) ? parsed.assignments[k] : [];
                restoredAssignments[k] = arr.map((x) => byId.get(x.id)).filter(Boolean) as AIDatasetDragItem[];
              });
            }
            const poolItems: AIDatasetDragItem[] = Array.isArray(parsed.pool)
              ? parsed.pool.map((x: any) => byId.get(x.id)).filter(Boolean) as AIDatasetDragItem[]
              : [];
            if (Object.keys(restoredAssignments).length || poolItems.length) {
              setState((prev) => ({
                pool: poolItems.length ? poolItems : prev.pool,
                lists: { ...prev.lists, ...restoredAssignments },
              }));
            }
          }
        } catch (_) {
          // ignore invalid workspace
        }
      })
      .catch(() => {/* ignore */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, initialItems]);

  const onDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      isDraggingRef.current = false;
      if (!result.destination) return;

      const destination = result.destination;

      if (
        result.source.droppableId === destination.droppableId &&
        result.source.index === destination.index
      ) {
        return;
      }
      // Compute updates atomically from previous state to avoid duplicates
      setState((prev) => {
        const next: ListsState = {
          pool: Array.from(prev.pool),
          lists: Object.fromEntries(Object.entries(prev.lists).map(([k, arr]) => [k, Array.from(arr)])),
        };

        const srcId = result.source.droppableId;
        const dstId = destination.droppableId;
        const srcIdx = result.source.index;
        const dstIdx = destination.index;

        const getRef = (id: string): AIDatasetDragItem[] => (id === 'pool' ? next.pool : (next.lists[id] || []));
        const setRef = (id: string, items: AIDatasetDragItem[]) => {
          if (id === 'pool') next.pool = items;
          else next.lists[id] = items;
        };

        const sourceList = getRef(srcId);
        if (srcIdx < 0 || srcIdx >= sourceList.length) return prev;
        const [moved] = sourceList.splice(srcIdx, 1);
        if (!moved) return prev;

        const destList = getRef(dstId);
        if (dstIdx < 0 || dstIdx > destList.length) return prev;

        // If reordering within same list, just insert back
        destList.splice(dstIdx, 0, moved);
        setRef(srcId, sourceList);
        setRef(dstId, destList);

        return next;
      });
    },
    [],
  );

  const handleValidate = () => {
    const hasMisplaced = Object.entries(state.lists).some(([key, items]) =>
      items.some((item) => item.expectedKey !== key),
    );

    const missingAssignments = state.pool.some((item) => item.expectedKey !== null);
    const noiseMisplaced = Object.values(state.lists).some((items) =>
      items.some((item) => item.expectedKey === null),
    );

    // Save workspace in all cases
    if (lessonId) {
      const payload = {
        workspace: JSON.stringify({
          configHash,
          assignments: Object.fromEntries(
            Object.entries(state.lists).map(([k, arr]) => [k, arr.map((it) => ({ id: it.id }))])
          ),
          pool: state.pool.map((it) => ({ id: it.id })),
          result: {
            hasMisplaced,
            missingAssignments,
            noiseMisplaced,
          },
        }),
      };
      try { saveUserAnswerById(lessonId, payload).catch(() => {/* ignore */}); } catch(_) { /* ignore */ }
    }

    if (hasMisplaced || missingAssignments || noiseMisplaced) {
      message.error(t('aiDataset.student.validationFailed'));
      return;
    }

    onComplete();
  };

  const handleReset = () => {
    setState({
      pool: initialItems,
      lists: Object.fromEntries(targets.map((target) => [target.id, []])),
    });
  };

  if (!targets.length) {
    return <Empty description={t('aiDataset.student.empty')} />;
  }

  const isImagePath = (v: string) => /\.(png|jpe?g|gif|svg|webp)$/i.test(v) || v.includes('/images/');
  const isAudioPath = (v: string) => /\.(mp3|wav|ogg|m4a)$/i.test(v) || v.includes('/sounds/') || v.includes('/audio/');

  const renderItem = (item: AIDatasetDragItem, index: number) => {
    const label = item.label || '';
    return (
      <Draggable draggableId={item.id} index={index} key={item.id}>
        {(provided, snapshot) => (
          <div
            className={`ai-dataset-draggable ${snapshot.isDragging ? 'ai-dataset-draggable--dragging' : ''}`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            {isImagePath(label) ? (
              // Disable native browser drag to avoid duplicate drags
              <img
                src={getImagePath(label)}
                alt="feature"
                className="ai-dataset-draggable-media"
                draggable={false}
              />
            ) : isAudioPath(label) ? (
              // Disable native browser drag and keep controls
              <audio
                src={getSoundPath(label)}
                controls
                className="ai-dataset-draggable-media"
                draggable={false}
                // react-beautiful-dnd starts drags from mousedown/touchstart:
                // stop those so clicking the audio controls doesn't lift the item
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              />
            ) : (
              <Typography.Text ellipsis={{ tooltip: label }}>
                {label}
              </Typography.Text>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  const renderDropZone = (droppableId: string, items: AIDatasetDragItem[]) => (
    <Droppable droppableId={droppableId} ignoreContainerClipping>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          className={`ai-dataset-dropzone ${snapshot.isDraggingOver ? 'ai-dataset-dropzone--active' : ''}`}
          {...provided.droppableProps}
        >
          {items.map((item, index) => renderItem(item, index))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <>
      {statement && (
        <div className="ai-dataset-statement">{statement}</div>
      )}
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="ai-dataset-player">
        <div className="ai-dataset-column ai-dataset-column--left">
          <Typography.Title level={4}>{t('aiDataset.student.keysTitle')}</Typography.Title>
          <div className="ai-dataset-keys">
            {targets.map((target) => {
              const label = target.label || '';
              const showImage = label && isImagePath(label);
              const showAudio = label && isAudioPath(label);
              return (
                <Card
                  key={target.id}
                  title={!showImage && !showAudio ? target.label : undefined}
                  className="ai-dataset-key-card"
                >
                  {showImage && (
                    <div className="ai-dataset-key-media">
                      <img src={getImagePath(label)} alt="key" draggable={false} />
                    </div>
                  )}
                  {showAudio && (
                    <div className="ai-dataset-key-media ai-dataset-key-media--audio">
                      <audio src={getSoundPath(label)} controls draggable={false} onPointerDown={(e) => e.stopPropagation()} />
                    </div>
                  )}
                  {renderDropZone(target.id, state.lists[target.id] || [])}
                </Card>
              );
            })}
          </div>
        </div>

        <div className="ai-dataset-column ai-dataset-column--right">
          <Typography.Title level={4}>{t('aiDataset.student.optionsTitle')}</Typography.Title>
          <Card>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('aiDataset.student.instructions')}
            </Typography.Paragraph>
            {renderDropZone('pool', state.pool)}
          </Card>

          <Space style={{ marginTop: 16 }}>
            <Button onClick={handleReset}>{t('aiDataset.student.reset')}</Button>
            <Button type="primary" onClick={handleValidate}>
              {t('aiDataset.student.validate')}
            </Button>
          </Space>
        </div>
        </div>
      </DragDropContext>
    </>
  );
};

export default AIDatasetLessonPlayer;
